import React, { useMemo } from 'react';
import { Card, Typography, Tag, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import type { RouteViewModelRoutePenalty } from '../../../types/routeViewModel';

const { Text } = Typography;

function formatRub(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })} ₽`;
}

function penaltyStatusLabel(status: string): string {
  const s = String(status || '').toUpperCase();
  if (s === 'CALCULATED') return 'Рассчитан';
  if (s === 'APPLIED') return 'Применён к финансам';
  if (!s) return '—';
  return status;
}

function statusTagColor(status: string): string {
  const s = String(status || '').toUpperCase();
  if (s === 'APPLIED') return 'green';
  if (s === 'CALCULATED') return 'gold';
  return 'default';
}

/** Только текст, введённый человеком; служебные пометки автоматики не показываем логисту. */
function getUserComment(comment: string | null | undefined): string | null {
  if (comment == null) return null;
  const s = String(comment).trim();
  if (!s) return null;
  if (s.startsWith('AUTO_')) return null;
  if (s.includes('task=')) return null;
  return s;
}

interface PenaltiesBlockProps {
  penalties: RouteViewModelRoutePenalty[] | undefined;
}

const PenaltiesBlock: React.FC<PenaltiesBlockProps> = ({ penalties }) => {
  const rows = useMemo(
    () => (Array.isArray(penalties) ? penalties : []),
    [penalties]
  );

  const { totalRub, count } = useMemo(() => {
    const total = rows.reduce((sum, p) => {
      const n = Number(p.calculatedAmount);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    return { totalRub: total, count: rows.length };
  }, [rows]);

  const cardTitle =
    count > 0 ? (
      <span>
        <WarningOutlined style={{ marginRight: 8 }} />
        Штрафы: {formatRub(totalRub)} ({count})
      </span>
    ) : (
      <span>
        <WarningOutlined style={{ marginRight: 8 }} />
        Штрафы
      </span>
    );

  return (
    <Card
      title={cardTitle}
      style={{ marginBottom: 16 }}
      size="small"
      styles={{ body: { paddingTop: count > 0 ? 8 : 12 } }}
    >
      {rows.length === 0 ? (
        <Text type="secondary">Штрафы отсутствуют</Text>
      ) : (
        <div>
          {rows.map((p, idx) => {
            const showDays = p.days != null && Number(p.days) > 0;
            const userComment = getUserComment(p.comment);
            const code = p.agreementPenalty?.code?.trim();
            const nameText = p.agreementPenalty?.name?.trim() || '—';

            const titleLine = (
              <Text strong>{nameText}</Text>
            );

            return (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '6px 0',
                  borderBottom: idx < rows.length - 1 ? '1px solid var(--ant-color-split, #f0f0f0)' : undefined
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ lineHeight: 1.35 }}>
                    {code ? (
                      <Tooltip title={`Код в системе: ${code}`}>
                        <span>{titleLine}</span>
                      </Tooltip>
                    ) : (
                      titleLine
                    )}
                  </div>
                  {showDays ? (
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                      {Number(p.days)} дн.
                    </Text>
                  ) : null}
                  {userComment ? (
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: 'block', marginTop: 4, maxWidth: '100%' }}
                      ellipsis={{ tooltip: userComment }}
                    >
                      {userComment}
                    </Text>
                  ) : null}
                </div>
                <div
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatRub(p.calculatedAmount)}
                  </Text>
                  <Tag color={statusTagColor(p.status)} style={{ margin: 0 }}>
                    {penaltyStatusLabel(p.status)}
                  </Tag>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default PenaltiesBlock;
